import React, { useEffect, useMemo, useState } from 'react'
import {
  Box, Tabs, Tab, TextField, Stack, Button, IconButton, Drawer, Typography,
  Alert, Snackbar, CircularProgress, MenuItem, Select, FormControl, InputLabel,
  Divider, Paper,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import EditIcon from '@mui/icons-material/Edit'
import CloseIcon from '@mui/icons-material/Close'
import SaveIcon from '@mui/icons-material/Save'
import { apiService } from '../services/api'

const OPERATORS = [
  { code: 'plk', label: 'Plus (PLK)' },
  { code: 'opl', label: 'Orange (OPL)' },
  { code: 'tmpl', label: 'T-Mobile (TMPL)' },
]

// Operator-specific column / form definitions kept lean — full schema is on
// the backend; here we only render the most useful fields.
const COLUMNS = {
  plk: [
    { field: 'sfid', headerName: 'SFID', width: 110, type: 'number' },
    { field: 'miasto', headerName: 'Miasto', width: 150 },
    { field: 'region', headerName: 'Region', width: 160 },
    { field: 'wlasnosc_pos', headerName: 'Własność', width: 100 },
    { field: 'nazwa_aps', headerName: 'Agent (firma)', width: 220 },
    { field: 'aps_podmiot_prowadzacy', headerName: 'Osoba prowadząca', width: 180 },
    { field: 'regionalny_koordynator_sprzedazy', headerName: 'RKS', width: 200 },
    { field: 'tel_kontaktowy_rks', headerName: 'Tel RKS', width: 120 },
    { field: 'kierownik_regionalny', headerName: 'KR', width: 180 },
  ],
  opl: [
    { field: 'plant_code', headerName: 'BSCS', width: 110 },
    { field: 'siec_sprzedazy', headerName: 'Sieć', width: 110 },
    { field: 'agent_code', headerName: 'Agent', width: 100 },
    { field: 'pelna_nazwa_agenta', headerName: 'Nazwa agenta', width: 240 },
    { field: 'rksa', headerName: 'RKSA', width: 200 },
    { field: 'rksa_email', headerName: 'Email RKSA', width: 220 },
    { field: 'xdm', headerName: 'XDM', width: 180 },
    { field: 'nazwa_regionu', headerName: 'Region', width: 130 },
    { field: 'nazwa_dystryktu', headerName: 'Dystrykt', width: 180 },
  ],
  tmpl: [
    { field: 'code', headerName: 'Kod POS', width: 130 },
    { field: 'agent_code', headerName: 'Kod agenta', width: 110 },
    { field: 'pelna_nazwa_agenta', headerName: 'Agent', width: 220 },
    { field: 'ownership_type', headerName: 'Typ', width: 120 },
    { field: 'agent_contact_phone', headerName: 'Telefon', width: 140 },
    { field: 'agent_contact_email', headerName: 'Email', width: 240 },
    { field: 'notes', headerName: 'Notatki', flex: 1, minWidth: 160 },
  ],
}

const EDITABLE_FIELDS = {
  plk: [
    'nazwa_aps', 'aps_podmiot_prowadzacy', 'tel_kontaktowy_aps', 'adres_email_aps',
    'regionalny_koordynator_sprzedazy', 'tel_kontaktowy_rks', 'adres_email_rks',
    'kierownik_regionalny', 'tel_kontaktowy_kr', 'adres_email_kr',
    'zastepca_kierownika_regionu', 'tel_kontaktowy_zkr', 'adres_email_zkr',
    'wlasnosc_pos',
  ],
  opl: [
    'siec_sprzedazy', 'agent_code', 'pelna_nazwa_agenta',
    'podmiot_prowadzacy', 'nazwa_podmiotu_prowadzacego',
    'rdsa', 'rdsa_email', 'rksa', 'rksa_email', 'opiekun_agenta_kam',
    'format', 'nazwa_regionu',
    'xdr', 'xdr_phone', 'xdr_email',
    'rds_d2d', 'rds_d2d_phone', 'rds_d2d_email',
    'nazwa_dystryktu', 'xdm', 'xdm_phone', 'xdm_email',
  ],
  tmpl: ['agent_code', 'pelna_nazwa_agenta', 'ownership_type',
         'agent_contact_phone', 'agent_contact_email', 'notes'],
}

const KEY_FIELD = { plk: 'sfid', opl: 'plant_code', tmpl: 'code' }
const HUMAN_LABEL = {
  // Common Polish-friendly labels for editor fields
  nazwa_aps: 'Agent — nazwa firmy',
  aps_podmiot_prowadzacy: 'APS — osoba prowadząca',
  tel_kontaktowy_aps: 'Tel APS',
  adres_email_aps: 'Email APS',
  regionalny_koordynator_sprzedazy: 'RKS',
  tel_kontaktowy_rks: 'Tel RKS',
  adres_email_rks: 'Email RKS',
  kierownik_regionalny: 'Kierownik Regionalny (KR)',
  tel_kontaktowy_kr: 'Tel KR',
  adres_email_kr: 'Email KR',
  zastepca_kierownika_regionu: 'Zastępca KR',
  tel_kontaktowy_zkr: 'Tel ZKR',
  adres_email_zkr: 'Email ZKR',
  wlasnosc_pos: 'Własność POS',
  siec_sprzedazy: 'Sieć Sprzedaży',
  agent_code: 'Kod agenta',
  pelna_nazwa_agenta: 'Pełna nazwa agenta',
  podmiot_prowadzacy: 'Podmiot prowadzący',
  nazwa_podmiotu_prowadzacego: 'Nazwa podmiotu prowadzącego',
  rdsa: 'RDSA',
  rdsa_email: 'Email RDSA',
  rksa: 'RKSA',
  rksa_email: 'Email RKSA',
  opiekun_agenta_kam: 'Opiekun Agenta (KAM)',
  format: 'Format',
  nazwa_regionu: 'Region',
  xdr: 'XDR (Dyrektor Komercyjny)',
  xdr_phone: 'Tel XDR',
  xdr_email: 'Email XDR',
  rds_d2d: 'Regionalny Dyrektor Sprzedaży D2D',
  rds_d2d_phone: 'Tel RDS D2D',
  rds_d2d_email: 'Email RDS D2D',
  nazwa_dystryktu: 'Dystrykt',
  xdm: 'XDM (Dystrykt Menedżer)',
  xdm_phone: 'Tel XDM',
  xdm_email: 'Email XDM',
  ownership_type: 'Typ własności',
  agent_contact_phone: 'Telefon agenta',
  agent_contact_email: 'Email agenta',
  notes: 'Notatki',
}

const AdminPosPage = () => {
  const [operator, setOperator] = useState('plk')
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [snack, setSnack] = useState(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)

  // Filters
  const [q, setQ] = useState('')
  const [posType, setPosType] = useState('')
  const [region, setRegion] = useState('')
  const [agent, setAgent] = useState('')
  const [rks, setRks] = useState('')
  const [distinct, setDistinct] = useState({})

  // Editor
  const [editing, setEditing] = useState(null)   // { item, fields }

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await apiService.getAdminPosList(operator, {
        q: q || undefined,
        pos_type: posType || undefined,
        region: region || undefined,
        agent: agent || undefined,
        rks: rks || undefined,
        page: page + 1,
        page_size: pageSize,
      })
      setItems(resp.data.items || [])
      setTotal(resp.data.total || 0)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally {
      setLoading(false)
    }
  }

  const loadDistinct = async () => {
    try {
      const resp = await apiService.getAdminPosDistinct(operator)
      setDistinct(resp.data || {})
    } catch {
      setDistinct({})
    }
  }

  useEffect(() => {
    setPage(0)
    setQ(''); setPosType(''); setRegion(''); setAgent(''); setRks('')
    loadDistinct()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operator])

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operator, page, pageSize])

  const onSearch = () => { setPage(0); load() }

  const startEdit = async (row) => {
    const key = row[KEY_FIELD[operator]]
    try {
      const resp = await apiService.getAdminPos(operator, key)
      setEditing({ key, item: resp.data, fields: { ...resp.data } })
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    }
  }

  const saveEdit = async () => {
    if (!editing) return
    const editable = EDITABLE_FIELDS[operator]
    const changed = {}
    for (const f of editable) {
      if (editing.fields[f] !== editing.item[f]) {
        changed[f] = editing.fields[f] === '' ? null : editing.fields[f]
      }
    }
    if (Object.keys(changed).length === 0) {
      setSnack({ severity: 'info', msg: 'Brak zmian.' })
      setEditing(null)
      return
    }
    try {
      await apiService.patchAdminPos(operator, editing.key, changed)
      setSnack({ severity: 'success', msg: `Zapisano (${Object.keys(changed).length} pól)` })
      setEditing(null)
      load()
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    }
  }

  const columns = useMemo(() => ([
    {
      field: '_actions', headerName: '', width: 56, sortable: false, filterable: false,
      renderCell: (p) => (
        <IconButton size="small" onClick={() => startEdit(p.row)} title="Edytuj">
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    },
    ...COLUMNS[operator],
  ]), [operator])

  const rows = useMemo(() => items.map((it, i) => ({
    id: it[KEY_FIELD[operator]] ?? i,
    ...it,
  })), [items, operator])

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Administracja POS — agent / RKS / kontakty</Typography>

      <Tabs value={operator} onChange={(_, v) => setOperator(v)} sx={{ mb: 2 }}>
        {OPERATORS.map(o => <Tab key={o.code} value={o.code} label={o.label} />)}
      </Tabs>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField
            size="small" label="Szukaj"
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            sx={{ minWidth: 240 }}
            placeholder={operator === 'plk' ? 'SFID / miasto / agent / RKS' : 'kod / agent / RKS'}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Typ</InputLabel>
            <Select label="Typ" value={posType} onChange={(e) => setPosType(e.target.value)}>
              <MenuItem value=""><em>Wszystkie</em></MenuItem>
              {(distinct.pos_types || []).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Region</InputLabel>
            <Select label="Region" value={region} onChange={(e) => setRegion(e.target.value)}>
              <MenuItem value=""><em>Wszystkie</em></MenuItem>
              {(distinct.regions || []).map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
          {(distinct.agents || []).length > 0 && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Agent</InputLabel>
              <Select label="Agent" value={agent} onChange={(e) => setAgent(e.target.value)}>
                <MenuItem value=""><em>Wszyscy</em></MenuItem>
                {(distinct.agents || []).map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {(distinct.rks || []).length > 0 && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>RKS</InputLabel>
              <Select label="RKS" value={rks} onChange={(e) => setRks(e.target.value)}>
                <MenuItem value=""><em>Wszyscy</em></MenuItem>
                {(distinct.rks || []).map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <Button variant="contained" onClick={onSearch} disabled={loading}>
            Szukaj
          </Button>
          <Button variant="text" onClick={() => {
            setQ(''); setPosType(''); setRegion(''); setAgent(''); setRks(''); setPage(0); load()
          }}>Wyczyść</Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ height: '70vh', width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          density="compact"
          rowCount={total}
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize) }}
          pageSizeOptions={[25, 50, 100, 200]}
          disableRowSelectionOnClick
        />
      </Box>

      <Drawer
        anchor="right" open={!!editing} onClose={() => setEditing(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
      >
        {editing && (
          <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6">
                Edycja {operator.toUpperCase()}: {editing.key}
              </Typography>
              <IconButton onClick={() => setEditing(null)}><CloseIcon /></IconButton>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {loading ? <CircularProgress /> : (
              <Stack spacing={2}>
                {EDITABLE_FIELDS[operator].map(f => (
                  <TextField
                    key={f}
                    size="small"
                    label={HUMAN_LABEL[f] || f}
                    value={editing.fields[f] ?? ''}
                    onChange={(e) => setEditing({ ...editing, fields: { ...editing.fields, [f]: e.target.value } })}
                    fullWidth
                  />
                ))}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button onClick={() => setEditing(null)}>Anuluj</Button>
                  <Button variant="contained" startIcon={<SaveIcon />} onClick={saveEdit}>Zapisz</Button>
                </Stack>
              </Stack>
            )}
          </Box>
        )}
      </Drawer>

      <Snackbar
        open={!!snack}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.msg}</Alert> : null}
      </Snackbar>
    </Box>
  )
}

export default AdminPosPage
